import { useEffect, useState } from 'react';
import { createFileRoute, useRouterState, Link } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import {
  useImpersonateUserMutationOptions,
  useUnsetImpersonateUserMutationOptions,
  usersQueryOptions,
} from '@/lib/query-options';
import { User } from '@/lib/types';
import { useToast } from '@/hooks/useToast';
import { setCookie } from '@/lib/utils';
import { openAsNewIdentity } from '@/lib/tab-organization';
import { useUserProfile } from '@/contexts/user.context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Button } from '@/components/ui/button';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';

export const Route = createFileRoute('/_authenticated/impersonate')({
  component: Impersonate,
});

function Impersonate() {
  const { isLoading, data: users = [] } = useSuspenseQuery(usersQueryOptions());
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [impersonateUser, setImpersonateUser] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const state = useRouterState({ select: (s) => s.location.state });
  const impersonateUserMutation = useImpersonateUserMutationOptions();
  const unsetImpersonateUserMutation = useUnsetImpersonateUserMutationOptions();
  const { user } = useUserProfile();

  useEffect(() => {
    const handleLogoutAsImpersonator = async () => {
      if (state?.impersonate?.action !== 'logout') return;

      try {
        const { access_token: token } = await unsetImpersonateUserMutation.mutateAsync();
        if (token) {
          setCookie('access_token', token, 1);
          // A new page, on no organisation (#7070): staying on this one kept the customer's cached data, atoms and
          // organisation, so the admin's next requests named the customer's organisation under the admin's token.
          // Not this page again: the router keeps the stop request in history.state, which a load of the same URL
          // keeps, so reloading here would stop again on every load.
          openAsNewIdentity('/notifications');
        }
      } catch (error) {
        toast({
          title: 'Impersonating failed Failed',
          description: 'Unable to impersonate due to an internal error. Please try again.',
        });
      }
    };

    handleLogoutAsImpersonator();
  }, [state]);

  const { toast } = useToast();

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <p>Loading...</p>
      </div>
    );
  }

  const { impersonating_email } = user;
  const filteredUsers = users
    .filter((item: User) => item.email !== user?.email)
    .filter(
      (item: User) =>
        item.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.organizations || []).some(org => org.toLowerCase().includes(searchQuery.toLowerCase())),
    );

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCheckboxChange = async (selectedUser: User) => {
    try {
      const data = await impersonateUserMutation.mutateAsync(selectedUser.email);
      const token = data?.access_token;
      if (token) {
        setCookie('access_token', token, 1);
        // A new page, on no organisation (#7070): the reload already reset the state, but the tab kept the admin's
        // organisation and opened the customer on it whenever the customer also belonged to it.
        openAsNewIdentity('/notifications');
      }
    } catch (error) {
      toast({
        title: 'Impersonating Failed',
        description: 'Unable to impersonate due to an internal error. Please try again.',
      });
    }
  };

  return (
    <div className='container mx-auto py-8 px-4 h-screen overflow-y-auto'>
      <div className='mb-6'>
        <Link to='/campaigns' className='inline-flex items-center text-sm text-muted-foreground hover:text-foreground'>
          <ArrowLeft className='mr-2 h-4 w-4' />
          Back
        </Link>
      </div>
      <Card className="text-card-foreground rounded-lg border bg-white p-6">
        <CardHeader>
          <CardTitle>Impersonate Users</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className='mb-6'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
              <Input
                placeholder='Search by name, email, or organization...'
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                className='pl-10'
              />
            </div>
          </div>
          <div className='overflow-x-auto'>
            <table className='w-full border-collapse'>
              <thead>
                <tr className='border-b'>
                  <th className='px-6 py-4 text-left font-medium'>Name</th>
                  <th className='px-6 py-4 text-left font-medium'>Email</th>
                  <th className='px-6 py-4 text-left font-medium'>Organizations</th>
                  <th className='px-6 py-4 text-left font-medium'>Role</th>
                  <th className='px-6 py-4 text-right font-medium'>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user: User) => (
                  <tr key={user.email} className='border-b hover:bg-gray-50'>
                    <td className='px-6 py-4'>{user.name}</td>
                    <td className='px-6 py-4'>{user.email}</td>
                    <td className='px-6 py-4 text-sm text-gray-500'>{(user.organizations || []).join(', ') || '-'}</td>
                    <td className='px-6 py-4'>{user.role ?? 'User'}</td>
                    <td className='px-6 py-4 text-right'>
                      <Button
                        color={impersonating_email === user.email ? 'indigo' : 'zinc'}
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setImpersonateUser(user);
                        }}
                        disabled={impersonating_email === user.email}
                        className='min-w-[120px]'
                      >
                        {impersonating_email === user.email ? 'Impersonating...' : 'Impersonate'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className='mt-4 flex justify-center'>
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
          )}
        </CardContent>
      </Card>
      <ConformationModal
        title='Impersonate User'
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setImpersonateUser(null);
        }}
        onConfirm={() => {
          handleCheckboxChange(impersonateUser ?? ({} as User));
        }}
      />
    </div>
  );
}
